import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { TranslateService } from '@ngx-translate/core';
import { UserNonConformancesRoutes } from 'src/app/core/enums/routes.enum';
import { SetDashboardModalOpen } from 'src/app/core/state/dashboard-plugins.actions';
import { ChangeCurrentSelectedDepartment } from 'src/app/tenant-selector/state/tenant-user-access.actions';
import {
  TenantUserAccessState,
  ALL_DEPS_TOKEN,
} from 'src/app/tenant-selector/state/tenant-user-access.state';
import { DeviationsOverTimeProcessingService } from '../services/deviations-over-time-processing.service';
import { DashboardListItem } from '../models/dashboard-list-item.model';

@Component({
  selector: 'app-dashboard-list-modal',
  templateUrl: './dashboard-list-modal.component.html',
  styleUrls: ['./dashboard-list-modal.component.scss'],
  standalone: false,
})
export class DashboardListModalComponent implements OnInit, OnDestroy {
  private rowDatesMap = new Map<string, Date | null>();
  sortedItems: DashboardListItem[] = [];

  get period(): string {
    return this.data.period;
  }

  get formattedTitle(): string {
    const periodStr = this.processingService.formatPeriodWithFullMonth(
      this.data.period,
      this.data.items,
      this.data.dateField
    );
    const titleKey = this.data.titleKey || 'nonConformances';
    const baseTitle = this.translateService.instant(titleKey);
    return periodStr ? `${baseTitle} - ${periodStr}` : baseTitle;
  }

  constructor(
    public dialogRef: MatDialogRef<DashboardListModalComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      period: string;
      items: DashboardListItem[];
      titleKey?: string;
      dateField?: string;
      sortByDate?: boolean;
    },
    private router: Router,
    private store: Store,
    private translateService: TranslateService,
    private processingService: DeviationsOverTimeProcessingService
  ) {
    this.initializeRowDates();
  }

  ngOnInit(): void {
    this.store.dispatch(new SetDashboardModalOpen(true));
  }

  ngOnDestroy(): void {
    this.store.dispatch(new SetDashboardModalOpen(false));
  }

  private initializeRowDates(): void {
    const dateField = this.data.dateField || 'nonConformanceDate';
    this.data.items.forEach((row) => {
      if (!(dateField in row) || !row[dateField]) {
        this.rowDatesMap.set(row.id, null);
        return;
      }

      const dateValue = row[dateField];
      const date = new Date(dateValue);
      const parsedDate = isNaN(date.getTime()) ? null : date;
      this.rowDatesMap.set(row.id, parsedDate);
    });

    // Sort items by date if sortByDate is true
    if (this.data.sortByDate) {
      this.sortedItems = [...this.data.items].sort((a, b) => {
        const dateA = this.rowDatesMap.get(a.id);
        const dateB = this.rowDatesMap.get(b.id);
        // Items without dates go to the bottom
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;
        return dateB.getTime() - dateA.getTime();
      });
    } else {
      this.sortedItems = [...this.data.items];
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  getRowDate(rowId: string): Date | null {
    return this.rowDatesMap.get(rowId) ?? null;
  }

  navigateToItem(row: DashboardListItem): void {
    const currentDepartmentId = this.store.selectSnapshot(
      TenantUserAccessState.currentDepartmentId
    );
    const allDeps = currentDepartmentId === ALL_DEPS_TOKEN;

    if (row.department?.id && (allDeps || currentDepartmentId !== row.department.id)) {
      this.store.dispatch(new ChangeCurrentSelectedDepartment(row.department.id, false, true));
    }

    const route = UserNonConformancesRoutes.ShowOverview.replace(':id', row.id);
    // TODO Woj: I mean most probably me, when this becomes more generic, simply add a @Input prop for route
    this.router.navigate([route]);
    this.dialogRef.close();
  }
}
